import _ from 'lodash';
import { templateLicenseMainForm } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { LicenseMainFormWebComponent } from '../component/LicenseMainFormWebComponent';
import { LicenseMainFormView } from '../view/LicenseMainFormView';
import { LicenseMainFormRepository } from '../repository/LicenseMainFormRepository';
import { SubscriptionType } from '../../types/SubscriptionType';
import { LicenseMainFormViewData } from '../types/LicenseMainFormViewData';
import { LicenseSubFormComponent } from '../../types/LicenseSubFormComponent';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CompanyType } from '../../../company/types/company-type';
import { CompanyLicensingTabHelper } from '../../../company/common/licensingTab/view/CompanyLicensingTabHelper';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { CompanyData } from '../../../company/types/company-data';
import { LicensePrePaidSubFormWebComponent } from '../../licensePrePaidSubForm';
import { DropdownEvents, SelectOption } from '../../../ui/gzDropdown/types';

export class LicenseMainFormController extends BaseWebComponentController {
  public component: LicenseMainFormWebComponent;
  public view: LicenseMainFormView;
  public viewConstructor: typeof LicenseMainFormView;
  public dataRepository: LicenseMainFormRepository;
  public dataRepositoryConstructor: typeof LicenseMainFormRepository;

  private validSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public valid$: Observable<boolean> = this.validSubject;

  private validSubFormSubscription: Subscription | null;

  private viewData: LicenseMainFormViewData;
  private company: CompanyData | null;
  private companyType: CompanyType; // in sync with the company type dropdown from the company details tab
  private subscriptionType: SubscriptionType; // in sync with the subscription type dropdown from the licensing tab

  constructor(component: LicenseMainFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: LicenseMainFormView,
      viewTemplate: templateLicenseMainForm,
      dataRepositoryConstructor: LicenseMainFormRepository,
    });
  }

  public async getDataForView(): Promise<{
    viewData: LicenseMainFormViewData;
    company: CompanyData | null;
  }> {
    const companyLicenseInformation =
      this.component.companyLicenseInformation ??
      (await this.dataRepository.getCompanyLicenseInformation(this.component.editedCompanyId));

    this.viewData = {
      company: this.component.company,
      context: this.component.context,
      companyLicenseInformation: companyLicenseInformation,
    };

    this.company = this.component.company;

    return {
      viewData: this.viewData,
      company: this.company,
    };
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$licensingOptionsDropdown,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onSubscriptionTypeChange,
      },
    ];
  }

  public getFormValue(): FormGroupValue {
    const activeLicenseSubForm = this.getActiveSubFormGroup();

    if (activeLicenseSubForm === null) {
      throw new Error(`No license-subform set for subscription type.`);
    }

    return _.merge(this.view.formGroup.value, activeLicenseSubForm.value);
  }

  public setCompanyType(companyType: CompanyType) {
    this.updateValidity(this.subscriptionType);
    this.companyType = companyType;
    if (this.view.dom.subformComponents.$monthlySubscriptionTrialSubForm.started) {
      this.view.dom.subformComponents.$monthlySubscriptionTrialSubForm.setCompanyType(companyType);
    }

    this.view.dom.subformComponents.$monthlySubscriptionSubForm.setCompanyType(companyType);

    this.updateSubscriptionOptions(companyType);
    this.view.setupLicenseSubFormComponents(companyType);
  }

  public getActiveSubscriptionType(): SubscriptionType | null {
    return this.subscriptionType ?? null;
  }

  public getLicenseSubForm(subscriptionType: SubscriptionType): LicenseSubFormComponent | null {
    const subFormMapping: { [subType in SubscriptionType]: LicenseSubFormComponent | null } = {
      [SubscriptionType.None]: null,
      [SubscriptionType.Trial]: this.view.dom.subformComponents.$trialSubForm,
      [SubscriptionType.Licensed]: this.view.dom.subformComponents.$prePaidSubForm,
      [SubscriptionType.Inherited]: this.view.dom.subformComponents.$monthlySubscriptionSubForm,
      [SubscriptionType.MonthlyLicenseTrial]: this.view.dom.subformComponents.$monthlyLicenseTrialSubForm,
      [SubscriptionType.InheritedMspTrial]: this.view.dom.subformComponents.$monthlySubscriptionTrialSubForm,
      [SubscriptionType.Frat]: this.view.dom.subformComponents.$fratSubForm,
      [SubscriptionType.BasicMonthly]: this.view.dom.subformComponents.$basicMonthlySubscriptionSubForm,
    };

    const subFormComponent = subFormMapping[subscriptionType];

    if (!subFormComponent?.started) {
      return null;
    }

    return subFormMapping[subscriptionType];
  }

  private updateSubscriptionOptions(companyType: CompanyType) {
    const selectedSubscriptionType = this.view.dom.$licensingOptionsDropdown.value as SubscriptionType;
    // if msp-trial was previous selected, licensing form must be reset once company switched to customer type
    if (companyType === CompanyType.Customer && selectedSubscriptionType === SubscriptionType.MonthlyLicenseTrial) {
      this.resetLicensingForm();
    }

    this.view.updateSubscriptionDropdownOptions(this.viewData, companyType, this.view.getCanManageNetworks());
  }

  private getActiveSubFormGroup(): LicenseSubFormComponent | null {
    const selectedSubscriptionType = this.view.dom.$licensingOptionsDropdown.value as SubscriptionType;
    return this.getLicenseSubForm(selectedSubscriptionType);
  }

  private updateValidity(subscriptionType: SubscriptionType) {
    const activeLicenseSubForm = this.getActiveSubFormGroup();

    this.validSubFormSubscription?.unsubscribe();

    if (!activeLicenseSubForm) {
      // no active subform means that overall it's not valid
      this.validSubject.next(false);
    } else {
      this.validSubFormSubscription = activeLicenseSubForm.valid$.subscribe({
        next: isValid => {
          if (subscriptionType === SubscriptionType.Licensed) {
            const licensePrePaidSubFormComponent = activeLicenseSubForm as LicensePrePaidSubFormWebComponent;
            const licenseKey = licensePrePaidSubFormComponent.getLicenseKeyFieldValue();
            const isLicenseKeyValid = licensePrePaidSubFormComponent.getLicenseKeyValidity();

            // check that the prepaid subform has issues, if so, disable the "Save/Next" button
            const hasIssues = licensePrePaidSubFormComponent.hasIssues;

            if (!licenseKey && this.companyType === CompanyType.Partner) {
              this.validSubject.next(true);
            } else {
              this.validSubject.next(isValid && isLicenseKeyValid && !hasIssues);
            }
          } else {
            this.validSubject.next(isValid);
          }
        },
      });
    }
  }

  private onSubscriptionTypeChange(event: WebComponentEvent): void {
    const { oldValue, newValue } = event.detail as { oldValue: SelectOption | null; newValue: SelectOption | null };

    if (oldValue && newValue && oldValue.value === newValue.value) {
      // subscription type did not change
      return;
    }

    this.subscriptionType = this.view.dom.$licensingOptionsDropdown.value as SubscriptionType;

    toggleVisibility(
      this.view.dom.$licenseTypeInfoMessage,
      this.subscriptionType === SubscriptionType.Licensed && !this.shouldDisplayPrePaidForm(this.subscriptionType)
    );

    this.updateValidity(this.subscriptionType);
    this.syncValuesBetweenMonthlySubForms();

    this.toggleSubformComponent(this.subscriptionType);
  }

  public resetLicensingForm(resetLicenseKeyForm: boolean = false): void {
    this.view.dom.$licensingOptionsDropdown.value = '';
    this.view.dom.$licensingOptionsDropdown.resetField();
    // hide current active subform
    this.toggleSubformComponent(SubscriptionType.None);

    if (resetLicenseKeyForm) {
      this.view.dom.subformComponents.$prePaidSubForm.resetLicensePrePaidForm();
    }
  }

  public shouldDisplayPrePaidForm(subscriptionType: SubscriptionType) {
    if (subscriptionType !== SubscriptionType.Licensed) {
      return false;
    }

    if (
      this.company &&
      CompanyLicensingTabHelper.shouldAddActionBeVisible(
        this.company,
        this.viewData.companyLicenseInformation,
        this.viewData.context
      ) &&
      this.viewData.context === CompanyActionContext.Edit
    ) {
      return false;
    }

    return true;
  }

  private syncValuesBetweenMonthlySubForms() {
    const isMonthlySelected = this.subscriptionType === SubscriptionType.Inherited;
    const isBasicSelected = this.subscriptionType === SubscriptionType.BasicMonthly;

    if (!isMonthlySelected && !isBasicSelected) {
      // sync is done only for: monthly <-> basic
      return;
    }

    const monthlySubform = this.view.dom.subformComponents.$monthlySubscriptionSubForm;
    const basicSubform = this.view.dom.subformComponents.$basicMonthlySubscriptionSubForm;

    if (isMonthlySelected && basicSubform.started) {
      // copy values from basic
      monthlySubform.setSubscriptionDetails(basicSubform.value);
    } else if (isBasicSelected && monthlySubform.started) {
      // copy values from monthly
      basicSubform.setSubscriptionDetails(monthlySubform.value);
    }
  }

  private toggleSubformComponent(subscriptionType: SubscriptionType) {
    toggleVisibility(this.view.dom.subformComponents.$trialSubForm, subscriptionType === SubscriptionType.Trial);
    toggleVisibility(this.view.dom.subformComponents.$fratSubForm, subscriptionType === SubscriptionType.Frat);
    const isPrePaidFormVisible = this.shouldDisplayPrePaidForm(subscriptionType);
    toggleVisibility(this.view.dom.subformComponents.$prePaidSubForm, isPrePaidFormVisible);
    toggleVisibility(
      this.view.dom.subformComponents.$basicMonthlySubscriptionSubForm,
      subscriptionType === SubscriptionType.BasicMonthly
    );
    toggleVisibility(
      this.view.dom.subformComponents.$monthlyLicenseTrialSubForm,
      subscriptionType === SubscriptionType.MonthlyLicenseTrial
    );
    toggleVisibility(
      this.view.dom.subformComponents.$monthlySubscriptionTrialSubForm,
      subscriptionType === SubscriptionType.InheritedMspTrial
    );
    toggleVisibility(
      this.view.dom.subformComponents.$monthlySubscriptionSubForm,
      subscriptionType === SubscriptionType.Inherited
    );
  }
}
